import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { ADD_FOLDER } from '../itemIds';
import { useEditorStore } from '~/shell/Store';
import AddFolderImg from '~/training/sequence/templates/items/quiz-match/components/addFolder.png';
import FolderEditModal from '../folder-box/FolderEditModal';
import PlusIcon from '~/assets/icons/plus.svg';

const AddFolderContainer = styled.div`
  border-radius: 22px;
  width: 220px;
  height: 175px;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  background-color: transparent;
  cursor: pointer;
`;

const AddFolderShape = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  color: #ffffff;
  background-image: url(${AddFolderImg});
  background-color: transparent;
  background-position: center;
  background-size: cover;
  padding: 16px;
  border-radius: 22px;
  height: 175px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: bold;
  line-height: 18px;
  text-align: center;
  border: none;
  gap: 8px;

  &:hover {
    background-color: transparent;
  }
`;

interface Props {
  label?: string;
}

export type AddFolderDefinition = ItemDefinition<typeof ADD_FOLDER, Props>;

export class AddFolder extends Item<typeof ADD_FOLDER, Props> {
  protected render({ label = 'Add Folder' }: RenderProps<Props>) {
    const { addSubItem, sequence, updateSequence } = useEditorStore();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Check if we can add more folders (max 6)
    const canAddFolder = () => {
      const questionItem = sequence.items.find(
        (item: { __typename?: string }) => item.__typename === 'Question'
      );
      const currentFolderCount = questionItem?.items?.length || 0;
      return currentFolderCount < 6;
    };

    const handleAddFolder = () => {
      if (!canAddFolder()) return;
      // Just open the modal first, don't create folder yet
      setIsModalOpen(true);
    };

    const handleSaveModal = (cardText: string, folderName: string) => {
      // Now create the folder with the data from the modal
      const questionItem = sequence.items.find(
        (item: { __typename?: string }) => item.__typename === 'Question'
      );

      if (questionItem) {
        const position = questionItem.items ? questionItem.items.length + 1 : 1;
        const tempId = `${Date.now()}`;

        const newItem = {
          id: tempId,
          feedback: folderName,
          position: position,
          correct: false,
          value: cardText
        };

        const newFolderData = {
          type: 'QUESTION_FIELD',
          feedback: folderName,
          title: cardText, // Use 'title' instead of 'value' to match updateSequence pattern
          value: cardText, // Keep both for compatibility
          correct: false,
          position: position,
          parent_id: questionItem.id
        };

        // Add the folder with the data from the modal
        addSubItem({
          sequenceId: sequence.id,
          data: newFolderData,
          newItem: newItem
        });
      }

      setIsModalOpen(false);
    };

    const handleCloseModal = () => {
      // If user cancels, just close modal without creating folder
      setIsModalOpen(false);
    };

    return (
      <>
        {/* Only show Add Folder button if we can add more folders */}
        {canAddFolder() && (
          <AddFolderContainer
            ref={containerRef}
            role="button"
            tabIndex={0}
            aria-label={label}
            onClick={handleAddFolder}
          >
            <AddFolderShape>
              <PlusIcon
                style={{
                  filter: 'brightness(0) invert(1)',
                  width: '20px',
                  height: '20px'
                }}
              />
              {label}
            </AddFolderShape>
          </AddFolderContainer>
        )}

        {/* Always render modal if it's open */}
        {isModalOpen &&
          ReactDOM.createPortal(
            <FolderEditModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              onSave={handleSaveModal}
              folderName=""
              existingQuestion=""
            />,
            document.body
          )}
      </>
    );
  }
}

registry[ADD_FOLDER] = AddFolder;
